import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { classNames } from '../../utils/classNames';
import { useTheme } from '../../utils/theme-context';
import { rome as moment } from '../../utils/date';
import Loading from '../../shared/Loading';
import { DEVICE_INFO_API_URL } from '../../config';

type TActive = 'copy' | 'thumb-up' | 'thumb-down' | 'save'

const MessageActions = ({
	chatId,
	message,
	liked,
	disliked,
	favorite,
	updatedAt,
	audioRef
}: {
	chatId: string,
	message: string,
	topicId?: string,
	liked?: boolean,
	disliked?: boolean,
	favorite?: boolean,
	updatedAt: string,
	audioRef?: MutableRefObject<HTMLAudioElement | { pause: () => void; paused: boolean; currentTime: number } | null>
}) => {
	const [isVoiceLoading, setIsVoiceLoading] = useState<boolean>();
	const { theme } = useTheme();
	const [active, setActive] = useState<TActive[]>([]);
	const [isSpeaking, setIsSpeaking] = useState(false);
	const abortControllerRef = useRef<AbortController | null>(null);
	
	const objectUrlRef = useRef<string | null>(null);

	useEffect(() => {
		setActive(prev => {
			const next = prev.filter(a => a !== 'thumb-up' && a !== 'thumb-down');
			if (liked) next.push('thumb-up');
			if (disliked) next.push('thumb-down');
			return next;
		});
	}, [liked, disliked, favorite])

	const stripHtml = (html: string): string => {
		const div = document.createElement('div');
		div.innerHTML = html;
		return div.textContent || div.innerText || '';
	};

	const cancelStreaming = () => {
		// On unmount, stop audio
		if (audioRef && audioRef.current) {
			audioRef.current.pause();
			audioRef.current = null;
		}

		// Abort the fetch stream
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		// Revoke any object URLs if needed
		if (objectUrlRef.current) {
			URL.revokeObjectURL(objectUrlRef.current);
			objectUrlRef.current = null;
		}
	}

	useEffect(() => {
		return () => {
			cancelStreaming();
		};
	}, []);

	const speakText = async () => {
		if (isVoiceLoading) return;

		try {
			cancelStreaming()

			setIsVoiceLoading(true);

			const controller = new AbortController();
			abortControllerRef.current = controller;

			const response = await fetch(
				`${DEVICE_INFO_API_URL}/tts/stream?text=${encodeURIComponent(message)}&voice=alloy`,
				{ signal: controller.signal }
			);

			if (!response.body) {
				throw new Error('No stream body in response');
			}

			const mimeCodec = 'audio/mpeg';
			if (!MediaSource.isTypeSupported(mimeCodec)) {
				throw new Error('Codec not supported');
			}

			const mediaSource = new MediaSource();
			const audio = new Audio();
			const objectUrl = URL.createObjectURL(mediaSource);
			objectUrlRef.current = objectUrl;
			audio.src = objectUrl;
			if (audioRef)
				audioRef.current = audio;

			mediaSource.addEventListener('sourceopen', async () => {
				const sourceBuffer = mediaSource.addSourceBuffer(mimeCodec);
				const reader = response.body!.getReader();

				const readChunk = async () => {
					try {
						const { done, value } = await reader.read();
						if (done) {
							mediaSource.endOfStream();
							return;
						}
						sourceBuffer.appendBuffer(value);
					} catch (e) {
					}
				};

				sourceBuffer.addEventListener('updateend', readChunk);
				await readChunk();
			});

			audio.play();
			setIsSpeaking(true);

			audio.addEventListener('ended', () => {
				setIsSpeaking(false);
			});

		} catch (e) {
			
		} finally {
			setIsVoiceLoading(false);
		}
	};

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(stripHtml(message));
			setActive((prev) => [...prev, 'copy']);
			setTimeout(() => {
				setActive((prev) => prev.filter((ac) => ac !== 'copy'));
			}, 2000);
		} catch (err) {
			console.error('copy error:', err);
		}
	};
	
	const btnClassNames = 'p-1 hover:bg-gray-200 rounded-md dark:hover:bg-gray-600';
	return (
		<div className='mt-1 flex flex-row items-center'>
			<p className='text-gray-400 font-semibold text-xs mr-2'>{moment(updatedAt).format('h:mm A')}</p>
			{/* <p className='mb-1'>Let me know how you find this response.</p> */}
			<button onClick={handleCopy} className={classNames(btnClassNames)} title='copy' type='button'>
				{active.includes('copy') ? <img alt='copy' src={theme === 'dark' ? '/svg/icons/copy-white.svg' : '/svg/icons/active/copy-active.svg'} className='object-contain w-[14px]' /> : <img alt='copy' src={theme === 'dark' ? '/svg/icons/copy-white.svg' : '/svg/icons/copy.svg'} className='object-contain w-4' />}
			</button>
			{/* <button onClick={handleThumbUp} className={classNames(btnClassNames, isLoading ? 'opacity-50' : '')} disabled={isReactLoading} title='thumbs up' type='button'>
				{active.includes('thumb-up') ? <img alt='thumb up' src={theme === 'dark' ? '/svg/icons/active/thumb-up-white.svg' : '/svg/icons/active/thumb-up.svg'} className='object-contain w-[22px]' /> : <img alt='thumb up' src={theme === 'dark' ? '/svg/icons/thumb-up-white.svg' : '/svg/icons/thumb-up.svg'} className='object-contain w-[22px]' />}
			</button>
			<button onClick={handleThumbDown} className={classNames(btnClassNames, isLoading ? 'opacity-50' : '')} disabled={isReactLoading} title='thumbs down' type='button'>
				{active.includes('thumb-down') ? <img alt='thumb down' src={theme === 'dark' ? '/svg/icons/active/thumb-down-white.svg' : '/svg/icons/active/thumb-down.svg'} className='object-contain w-[22px]' /> : <img alt='thumb up' src={theme === 'dark' ? '/svg/icons/thumb-down-white.svg' : '/svg/icons/thumb-down.svg'} className='object-contain w-[22px]' />}
			</button>
			<button onClick={handleSave} className={classNames(btnClassNames, isLoading ? 'opacity-50' : '')} disabled={isLoading} title='save' type='button'>
				{active.includes('save') ? <img alt='save' src={theme === 'dark' ? '/svg/icons/active/save-active-white.svg' : '/svg/icons/active/save-active.svg'} className='object-contain w-[22px]' /> : <img alt='thumb up' src={theme === 'dark' ? '/svg/icons/save-white.svg' : '/svg/icons/save.svg'} className='object-contain w-[22px]' />}
			</button> */}
			<button onClick={speakText} className={btnClassNames} style={{opacity: isVoiceLoading ? '0.5' : '1'}} title='Read aloud'>
				{isVoiceLoading ? <Loading /> : isSpeaking ? <img alt='audio' src={theme === 'dark' ? '/svg/icons/active/audio-active-white.svg' : '/svg/icons/active/audio-active.svg'} className='object-contain w-[14px]' /> : <img alt='audio' src={theme === 'dark' ? '/svg/icons/audio-white.svg' : '/svg/icons/audio.svg'} className='object-contain w-4' />}
			</button>
		</div>
	)
}

export default MessageActions;